/* ==========================================================================
   SIMPATICO PDP  —  sim-variant-backorder.js
   --------------------------------------------------------------------------
   Makes availability honest on multi-variant products.

   WHY THIS EXISTS
   `variant.available` is TRUE for an out-of-stock variant set to "continue
   selling", so sim-pdp.js paints "In stock" on a backordered variant. On a
   single-variant product that never showed, because the server-rendered
   markup was already correct for the only variant there was. The moment one
   product carries a stocked variant AND a backordered one — Conspit ARES 12,
   CDR in stock, CDP on backorder — selecting the backordered variant left an
   "In stock" label, a one-business-day dispatch promise and an "Add to cart"
   button on an item that is weeks away.

   Every variant's real backorder state is emitted server-side into the
   [data-sim-variants] JSON by sim-product-main.liquid (bo_state, bo_sentence,
   both straight out of snippets/backorder-meta.liquid, which stays the single
   source of truth for lead times). This file listens for the variant change
   that sim-pdp.js already publishes and corrects what that paint pass got
   wrong.

   It runs AFTER sim-pdp.js on purpose. `sim:variant:change` is dispatched at
   the very end of its paint(), so everything set here is final.

   Touches nothing on a product whose variants are all in the same state, and
   does nothing at all when the JSON carries no bo_state — safe to leave in
   place on every product.
   ========================================================================== */
(function () {
  'use strict';

  var BOOTED = 'data-sim-bo-booted';

  function isBackordered(state) {
    return state === 'backorder' || state === 'preorder';
  }

  function initSection(root) {
    if (root.getAttribute(BOOTED) === 'true') return;

    var dataEl = root.querySelector('[data-sim-variants]');
    if (!dataEl) return;

    var variants;
    try { variants = JSON.parse(dataEl.textContent || '[]'); } catch (e) { return; }
    if (!variants.length) return;

    // Nothing to correct on a product where no variant reports a state.
    var hasState = variants.some(function (v) { return typeof v.bo_state === 'string'; });
    if (!hasState) return;

    root.setAttribute(BOOTED, 'true');

    var byId = {};
    variants.forEach(function (v) { byId[String(v.id)] = v; });

    var stockEl    = root.querySelector('[data-sim-stock]');
    var stockRow   = stockEl ? stockEl.closest('[data-sim-stock-row]') : null;
    var shipLine   = root.querySelector('[data-sim-ship][data-sim-ship-threshold]');
    var shipOk     = shipLine ? shipLine.querySelector('.sim-ship__ok') : null;
    var shipInStock = shipOk ? shipOk.textContent : '';
    var submit     = root.querySelector('[data-sim-submit]');
    var submitTxt  = submit ? submit.querySelector('[data-sim-submit-text]') : null;
    var availInput = root.querySelector('[data-sim-bo-availability]');
    var notices    = root.querySelectorAll('[data-sim-bo-notice-for]');
    var optLabels  = root.querySelectorAll('[data-sim-option-selected]');

    // The dispatch line's in-stock copy is whatever the theme setting says.
    // If the page happened to load on a backordered variant, that copy is the
    // lead-time sentence rather than the in-stock one, so recover the real
    // in-stock wording from the setting carried on the block.
    var first = variants[0];
    if (first && isBackordered(first.bo_state)) {
      var carrier = root.querySelector('[data-sim-ship-in-stock]');
      shipInStock = (carrier && carrier.getAttribute('data-sim-ship-in-stock')) || shipInStock;
    }

    function labelFor(v) {
      if (!submit) return '';
      if (v.bo_state === 'preorder') {
        return submit.getAttribute('data-preorder-label') || 'Pre-order Yours';
      }
      if (v.bo_state === 'backorder') {
        return submit.getAttribute('data-backorder-label') || 'Reserve Yours';
      }
      return submit.getAttribute('data-add-label') || 'Add to cart';
    }

    function apply(v) {
      if (!v) return;
      var bo = isBackordered(v.bo_state);

      // Availability row
      if (stockEl) {
        if (bo) {
          stockEl.textContent = v.bo_state === 'preorder' ? 'Pre-order' : 'Backordered';
          if (stockRow) stockRow.setAttribute('data-state', 'backorder');
        } else if (stockRow) {
          stockRow.setAttribute('data-state', v.available ? 'in' : 'out');
        }
      }

      // Dispatch line
      if (shipOk) {
        shipOk.textContent = bo && v.bo_sentence ? v.bo_sentence + '.' : shipInStock;
        shipOk.hidden = false;
      }
      if (shipLine) {
        shipLine.classList.toggle('sim-ship--backorder', bo);
        shipLine.hidden = false;
      }

      // Buy button
      if (submit) {
        var label = labelFor(v);
        submit.setAttribute('data-label', label);
        if (submitTxt && v.available) submitTxt.textContent = label;
      }

      // What the customer was promised, written onto the order. Disabled when
      // there is nothing to promise, because a disabled input is not submitted
      // and an empty Availability property must never reach an order.
      if (availInput) {
        var promise = bo && v.bo_sentence ? v.bo_sentence : '';
        availInput.value = promise;
        availInput.disabled = promise === '';
      }

      // Backorder notice for this variant only.
      Array.prototype.forEach.call(notices, function (n) {
        n.hidden = n.getAttribute('data-sim-bo-notice-for') !== String(v.id);
      });

      // Keep the "Quick Release  CDR" style selected-value labels in sync.
      Array.prototype.forEach.call(optLabels, function (el) {
        var i = parseInt(el.getAttribute('data-sim-option-selected'), 10) - 1;
        if (v.options && typeof v.options[i] === 'string') el.textContent = v.options[i];
      });
    }

    document.addEventListener('sim:variant:change', function (e) {
      var v = e.detail && e.detail.variant;
      if (!v) return;
      apply(byId[String(v.id)] || v);
    });

    // Run once for the variant the page loaded on. The server-rendered markup
    // is already correct for it, so this changes nothing visible; it is here to
    // settle the Availability input's disabled state before anyone can submit.
    var idInput = root.querySelector('[data-sim-variant-id]');
    var current = idInput ? byId[String(idInput.value)] : null;
    apply(current || variants[0]);
  }

  function boot(scope) {
    var root = scope || document;
    if (root.nodeType === 1 && root.matches && root.matches('[data-sim-section]')) initSection(root);
    Array.prototype.forEach.call(root.querySelectorAll('[data-sim-section]'), initSection);
  }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', function () { boot(); });

  document.addEventListener('shopify:section:load', function (e) {
    Array.prototype.forEach.call(
      e.target.querySelectorAll('[' + BOOTED + ']'),
      function (s) { s.removeAttribute(BOOTED); }
    );
    boot(e.target);
  });
})();
